import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Key } from 'react';
import axios from 'axios';
import { generateErrorMsg, sendNotification } from 'utils/helperMethods';
import { customGroupsUrls } from 'config/urls';
import { queryClient } from 'services/reactQueryService';
import { ErrorMessages } from 'utils/Enums';
import useFilterState from './useFilterState';
import { CREATE_NEW_GROUP_VALUE, CUSTOM_GROUPS_QUERY_KEY } from '../constants';
import type { CustomProductGroupRecord, Dim1Option, GroupOption } from '../types';

interface FilterKey {
    label: string;
    value: string;
    dimension: string;
}

interface UseCustomProductGroupModalProps {
    modalFilterKeys: FilterKey[];
    onMutationSuccess?: () => void;
}

function useCustomProductGroupModal({ modalFilterKeys, onMutationSuccess }: UseCustomProductGroupModalProps) {
    const modalFilter = useFilterState(modalFilterKeys);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState<CustomProductGroupRecord | null>(null);

    const [selectedDim2Id, setSelectedDim2Id] = useState<number | undefined>();
    const [customerGroups, setCustomerGroups] = useState<GroupOption[]>([]);
    const [customerGroupsLoading, setCustomerGroupsLoading] = useState(false);
    const [selectedGroupValue, setSelectedGroupValue] = useState<string | undefined>();

    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupDesc, setNewGroupDesc] = useState('');

    const [dim1List, setDim1List] = useState<Dim1Option[]>([]);
    const [dim1Loading, setDim1Loading] = useState(false);
    const [selectedDim1Keys, setSelectedDim1Keys] = useState<Key[]>([]);

    const [upsertLoading, setUpsertLoading] = useState(false);

    const mfFilterChangeCount = useRef(0);

    const isCreatingNew = selectedGroupValue === CREATE_NEW_GROUP_VALUE;
    const showGroupFields = !!selectedGroupValue;

    const groupSelectOptions = useMemo(() => {
        const createOption = { label: '+ Create New Group', value: CREATE_NEW_GROUP_VALUE };
        const existingOptions = customerGroups.map((g) => ({ label: g.group_name, value: String(g.group_id) }));
        return [createOption, ...existingOptions];
    }, [customerGroups]);

    const fetchGroupsForCustomer = useCallback(async (dim2Id: number) => {
        setCustomerGroupsLoading(true);
        setCustomerGroups([]);
        try {
            const { data } = await axios.get<GroupOption[]>(customGroupsUrls.listGroupsUrl(dim2Id));
            setCustomerGroups(Array.isArray(data) ? data : (data as any)?.groups ?? []);
        } catch {
            sendNotification('error', 'Failed to load groups.');
        } finally {
            setCustomerGroupsLoading(false);
        }
    }, []);

    const fetchDim1Options = useCallback(
        async (groupId?: number, filters: any = {}) => {
            setDim1Loading(true);
            setDim1List([]);
            try {
                const { data } = await axios.post<{ options: Dim1Option[]; total: number }>(
                    customGroupsUrls.postDim1OptionsUrl,
                    {
                        dim2_id: selectedDim2Id,
                        limit: 1000,
                        offset: 0,
                        group_id: groupId ?? 0,
                        filters,
                        search_key: '',
                    },
                );
                setDim1List(data?.options ?? []);
            } catch {
                sendNotification('error', 'Failed to load material groups.');
            } finally {
                setDim1Loading(false);
            }
        },
        [selectedDim2Id],
    );

    // Re-fetch dim1 options when modal filters change
    useEffect(() => {
        if (!isModalOpen || selectedDim2Id == null || !showGroupFields) return;
        mfFilterChangeCount.current += 1;
        if (mfFilterChangeCount.current <= 1) return;
        const groupId = !isCreatingNew && selectedGroupValue ? Number(selectedGroupValue) : undefined;
        fetchDim1Options(isCreatingNew ? undefined : groupId, modalFilter.currentFilters);
    }, [modalFilter.currentFilters]);

    const resetModalState = useCallback(() => {
        setSelectedDim2Id(undefined);
        setCustomerGroups([]);
        setSelectedGroupValue(undefined);
        setNewGroupName('');
        setNewGroupDesc('');
        setDim1List([]);
        setSelectedDim1Keys([]);
        modalFilter.reset();
        mfFilterChangeCount.current = 0;
    }, [modalFilter.reset]);

    const openAddModal = useCallback(() => {
        setEditingRecord(null);
        resetModalState();
        setIsModalOpen(true);
    }, [resetModalState]);

    const openEditModal = useCallback(
        async (record: CustomProductGroupRecord) => {
            setEditingRecord(record);
            resetModalState();
            setIsModalOpen(true);
            setSelectedDim2Id(record.dim2_id);

            setCustomerGroupsLoading(true);
            setDim1Loading(true);
            try {
                const [groupsRes, groupRes, dim1Res] = await Promise.all([
                    axios.get(customGroupsUrls.listGroupsUrl(record.dim2_id)),
                    axios.get(customGroupsUrls.getGroupUrl(record.group_id)),
                    axios.post<{ options: Dim1Option[]; total: number }>(customGroupsUrls.postDim1OptionsUrl, {
                        dim2_id: record.dim2_id,
                        limit: 1000,
                        offset: 0,
                        group_id: record.group_id,
                        filters: {},
                        search_key: '',
                    }),
                ]);
                setCustomerGroups(
                    Array.isArray(groupsRes.data) ? groupsRes.data : (groupsRes.data as any)?.groups ?? [],
                );
                setSelectedGroupValue(String(record.group_id));
                setNewGroupName(groupRes.data?.group_name ?? '');
                setNewGroupDesc(groupRes.data?.description ?? '');
                setDim1List(dim1Res.data?.options ?? []);
                setSelectedDim1Keys((groupRes.data?.dim1_ids ?? []).map(Number));
            } catch {
                sendNotification('error', 'Failed to load group details.');
            } finally {
                setCustomerGroupsLoading(false);
                setDim1Loading(false);
            }
        },
        [resetModalState],
    );

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
        setEditingRecord(null);
        resetModalState();
    }, [resetModalState]);

    const handleCustomerChange = useCallback(
        (dim2Id: any, _option: any) => {
            setSelectedDim2Id(dim2Id as number);
            setSelectedGroupValue(undefined);
            setDim1List([]);
            setSelectedDim1Keys([]);
            setNewGroupName('');
            setNewGroupDesc('');
            if (dim2Id) fetchGroupsForCustomer(dim2Id);
        },
        [fetchGroupsForCustomer],
    );

    const handleGroupChange = useCallback(
        async (val: any) => {
            setSelectedGroupValue(val as string);
            setSelectedDim1Keys([]);
            setDim1List([]);
            setNewGroupName('');
            setNewGroupDesc('');
            modalFilter.reset();
            mfFilterChangeCount.current = 0;
            if (val === CREATE_NEW_GROUP_VALUE) {
                fetchDim1Options(undefined, {});
            } else {
                const group = customerGroups.find((g) => String(g.group_id) === val);
                if (!group) return;
                setDim1Loading(true);
                try {
                    const [groupRes, dim1Res] = await Promise.all([
                        axios.get(customGroupsUrls.getGroupUrl(group.group_id)),
                        axios.post<{ options: Dim1Option[]; total: number }>(customGroupsUrls.postDim1OptionsUrl, {
                            dim2_id: selectedDim2Id,
                            limit: 1000,
                            offset: 0,
                            group_id: group.group_id,
                            filters: {},
                            search_key: '',
                        }),
                    ]);
                    setNewGroupName(groupRes.data?.group_name ?? '');
                    setNewGroupDesc(groupRes.data?.description ?? '');
                    const preSelectedKeys: Key[] = (groupRes.data?.dim1_ids ?? []).map(Number);
                    setDim1List(dim1Res.data?.options ?? []);
                    setSelectedDim1Keys(preSelectedKeys);
                } catch {
                    sendNotification('error', 'Failed to load group details.');
                } finally {
                    setDim1Loading(false);
                }
            }
        },
        [customerGroups, selectedDim2Id, modalFilter.reset, fetchDim1Options],
    );

    const handleSave = useCallback(async () => {
        if (!selectedDim2Id) {
            sendNotification('error', 'Please select a customer.');
            return;
        }
        if (!selectedGroupValue) {
            sendNotification('error', 'Please select a group.');
            return;
        }
        if (isCreatingNew && !newGroupName.trim()) {
            sendNotification('error', 'Please enter a new group name.');
            return;
        }
        if (selectedDim1Keys.length === 0) {
            sendNotification('error', 'Please select at least one material group.');
            return;
        }

        const userEmail = localStorage.getItem('email') || '';
        const payload = isCreatingNew
            ? {
                  dim2_id: selectedDim2Id,
                  group_name: newGroupName.trim(),
                  description: newGroupDesc.trim() || undefined,
                  dim1_ids: selectedDim1Keys.map(Number),
                  user_email: userEmail,
              }
            : {
                  group_name: newGroupName.trim(),
                  description: newGroupDesc.trim() || undefined,
                  dim1_ids: selectedDim1Keys.map(Number),
                  user_email: userEmail,
              };

        const url = isCreatingNew
            ? customGroupsUrls.createGroupUrl
            : customGroupsUrls.updateGroupUrl(Number(selectedGroupValue));

        setUpsertLoading(true);
        try {
            if (isCreatingNew) {
                await axios.post(url, payload);
            } else {
                await axios.patch(url, payload);
            }
            sendNotification('success', isCreatingNew ? 'Group created successfully.' : 'Group updated successfully.');
            queryClient.removeQueries({ queryKey: [CUSTOM_GROUPS_QUERY_KEY], exact: false });
            onMutationSuccess?.();
            closeModal();
        } catch (error: any) {
            sendNotification(
                'error',
                generateErrorMsg(String(error?.response?.data?.detail || ''), ErrorMessages.GENERALIZED_ERROR_MSG),
            );
        } finally {
            setUpsertLoading(false);
        }
    }, [selectedDim2Id, selectedGroupValue, isCreatingNew, newGroupName, newGroupDesc, selectedDim1Keys, closeModal, onMutationSuccess]);

    return {
        modalFilter,
        isModalOpen,
        editingRecord,
        selectedDim2Id,
        customerGroups,
        customerGroupsLoading,
        selectedGroupValue,
        groupSelectOptions,
        newGroupName,
        setNewGroupName,
        newGroupDesc,
        setNewGroupDesc,
        dim1List,
        dim1Loading,
        selectedDim1Keys,
        setSelectedDim1Keys,
        upsertLoading,
        isCreatingNew,
        showGroupFields,
        openAddModal,
        openEditModal,
        closeModal,
        handleCustomerChange,
        handleGroupChange,
        handleSave,
    };
}

export default useCustomProductGroupModal;
